/**
 * WebSocket hook for real-time alerts
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import { analytics } from '../lib/analytics';

const WS_URL = import.meta.env.VITE_WS_URL || 'wss://api.skop.site/api/alerts/subscribe';
const RECONNECT_DELAY = 3000;
const PING_INTERVAL = 30000;

export type AlertType = 'price_alert' | 'whale_activity' | 'new_token' | 'graduation';

export interface Alert {
  id: string;
  type: AlertType;
  title: string;
  message: string;
  tokenAddress?: string;
  tokenSymbol?: string;
  timestamp: number;
  data?: Record<string, unknown>;
}

export interface WebSocketState {
  connected: boolean;
  connecting: boolean;
  error: string | null;
}

interface UseWebSocketOptions {
  walletAddress?: string;
  onAlert?: (alert: Alert) => void;
  autoConnect?: boolean;
}

export function useWebSocket(options: UseWebSocketOptions = {}) {
  const { walletAddress, onAlert, autoConnect = true } = options;

  const wsRef = useRef<WebSocket | null>(null);
  const pingIntervalRef = useRef<number | null>(null);
  const reconnectTimeoutRef = useRef<number | null>(null);
  const subscribedTokensRef = useRef<Set<string>>(new Set());
  const subscribedWalletsRef = useRef<Set<string>>(new Set());

  const [state, setState] = useState<WebSocketState>({
    connected: false,
    connecting: false,
    error: null,
  });

  const [alerts, setAlerts] = useState<Alert[]>([]);

  const clearTimers = useCallback(() => {
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, []);

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    setState(s => ({ ...s, connecting: true, error: null }));

    try {
      const url = new URL(WS_URL);
      if (walletAddress) {
        url.searchParams.set('wallet', walletAddress);
      }

      const ws = new WebSocket(url.toString());
      wsRef.current = ws;

      ws.onopen = () => {
        setState({ connected: true, connecting: false, error: null });
        analytics.wsConnected();

        // Start ping interval
        pingIntervalRef.current = window.setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          }
        }, PING_INTERVAL);

        // Re-subscribe to previous subscriptions
        if (subscribedTokensRef.current.size > 0) {
          ws.send(JSON.stringify({
            type: 'subscribe_tokens',
            tokens: Array.from(subscribedTokensRef.current),
          }));
        }
        if (subscribedWalletsRef.current.size > 0) {
          ws.send(JSON.stringify({
            type: 'subscribe_wallets',
            wallets: Array.from(subscribedWalletsRef.current),
          }));
        }
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data) as {
            type: string;
            data?: Record<string, unknown>;
            timestamp?: number;
          };

          switch (message.type) {
            case 'connected':
              // Connection confirmed
              break;

            case 'subscribed':
              // Subscription confirmed
              break;

            case 'pong':
              // Heartbeat response
              break;

            case 'price_alert':
            case 'whale_activity':
            case 'new_token':
            case 'graduation': {
              const alert: Alert = {
                id: `${message.type}-${Date.now()}-${Math.random().toString(36).slice(2)}`,
                type: message.type as AlertType,
                title: getAlertTitle(message.type, message.data),
                message: getAlertMessage(message.type, message.data),
                tokenAddress: message.data?.tokenAddress as string | undefined,
                tokenSymbol: message.data?.tokenSymbol as string | undefined,
                timestamp: message.timestamp || Date.now(),
                data: message.data,
              };

              setAlerts(prev => [alert, ...prev].slice(0, 50)); // Keep last 50 alerts
              onAlert?.(alert);
              break;
            }
          }
        } catch {
          // Ignore parse errors
        }
      };

      ws.onclose = () => {
        setState(s => ({ ...s, connected: false, connecting: false }));
        analytics.wsDisconnected();
        clearTimers();

        // Auto-reconnect
        reconnectTimeoutRef.current = window.setTimeout(() => {
          analytics.wsReconnected();
          connect();
        }, RECONNECT_DELAY);
      };

      ws.onerror = () => {
        setState(s => ({ ...s, error: 'Connection error', connecting: false }));
      };
    } catch (err) {
      setState({
        connected: false,
        connecting: false,
        error: err instanceof Error ? err.message : 'Failed to connect',
      });
    }
  }, [walletAddress, onAlert, clearTimers]);

  const disconnect = useCallback(() => {
    clearTimers();
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setState({ connected: false, connecting: false, error: null });
  }, [clearTimers]);

  const subscribeTokens = useCallback((tokens: string[]) => {
    tokens.forEach(t => subscribedTokensRef.current.add(t));
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'subscribe_tokens', tokens }));
    }
  }, []);

  const unsubscribeTokens = useCallback((tokens: string[]) => {
    tokens.forEach(t => subscribedTokensRef.current.delete(t));
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'unsubscribe_tokens', tokens }));
    }
  }, []);

  const subscribeWallets = useCallback((wallets: string[]) => {
    wallets.forEach(w => subscribedWalletsRef.current.add(w));
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'subscribe_wallets', wallets }));
    }
  }, []);

  const unsubscribeWallets = useCallback((wallets: string[]) => {
    wallets.forEach(w => subscribedWalletsRef.current.delete(w));
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'unsubscribe_wallets', wallets }));
    }
  }, []);

  const clearAlerts = useCallback(() => {
    setAlerts([]);
  }, []);

  const dismissAlert = useCallback((alertId: string) => {
    setAlerts(prev => prev.filter(a => a.id !== alertId));
  }, []);

  // Auto-connect on mount
  useEffect(() => {
    if (autoConnect) {
      connect();
    }

    return () => {
      disconnect();
    };
  }, [autoConnect, connect, disconnect]);

  return {
    ...state,
    alerts,
    connect,
    disconnect,
    subscribeTokens,
    unsubscribeTokens,
    subscribeWallets,
    unsubscribeWallets,
    clearAlerts,
    dismissAlert,
  };
}

function getAlertTitle(type: string, data?: Record<string, unknown>): string {
  const symbol = data?.tokenSymbol || 'Token';
  switch (type) {
    case 'price_alert':
      return `Price Alert: ${symbol}`;
    case 'whale_activity':
      return `Whale Activity: ${symbol}`;
    case 'new_token':
      return `New Token: ${symbol}`;
    case 'graduation':
      return `Graduation: ${symbol}`;
    default:
      return 'Alert';
  }
}

function getAlertMessage(type: string, data?: Record<string, unknown>): string {
  switch (type) {
    case 'price_alert': {
      const change = data?.priceChange as number;
      if (change > 0) {
        return `Price increased by ${change.toFixed(1)}%`;
      } else {
        return `Price decreased by ${Math.abs(change).toFixed(1)}%`;
      }
    }
    case 'whale_activity': {
      const action = data?.action as string;
      const amount = data?.amount as number;
      return `Whale ${action || 'moved'} ${amount?.toLocaleString() || 'tokens'}`;
    }
    case 'new_token':
      return `New token launched on ${data?.platform || 'pump.fun'}`;
    case 'graduation':
      return `Token graduated to ${data?.dex || 'Raydium'}!`;
    default:
      return data?.message as string || 'New activity detected';
  }
}
